/**
 * FL-47: 管理者ユーザが自社のユーザを登録できる機能
 * CompanyUserService - 会社内ユーザー管理サービス
 */

import { SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from '../utils/supabase';
import { Role } from '../types';
import { DataServiceError, ErrorCodes } from './dataService';
import { SupabaseAuthService } from './supabase/supabaseAuthService';
import logger from '../utils/logger';

export interface CompanyUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  companyId: string;
  status?: string;
  landTransporterId?: string;
  lastLoginAt?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
}

export interface CreateUserData {
  email: string;
  name: string;
  role: Role;
  companyId: string;
}

export interface UpdateUserData {
  name?: string;
  role?: Role;
  password?: string; // FL-209: パスワード更新対応（オプション）
}

export interface InvitationData {
  email: string;
  role: Role;
  companyId: string;
}

export interface UserInvitation {
  id: string;
  email: string;
  role: Role;
  companyId: string;
  invitedBy: string;
  invitation_token: string;
  expiresAt: string;
  acceptedAt?: string;
  createdAt: string;
}

export class CompanyUserService {
  private supabase: SupabaseClient | null;

  constructor() {
    this.supabase = getSupabaseClient();
  }

  /**
   * Supabaseクライアントの可用性チェック
   */
  private ensureSupabaseClient(): SupabaseClient {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.CLIENT_INIT_FAILED
      );
    }
    return this.supabase;
  }

  /**
   * SupabaseAuthServiceの取得（パスワード更新用）
   */
  private getAuthService() {
    return new SupabaseAuthService();
  }

  /**
   * ユーザー作成（Supabase Auth + company_usersテーブル）
   */
  async createUser(
    userData: CreateUserData,
    currentUserId: string,
    password?: string
  ): Promise<CompanyUser> {
    // バリデーション
    this.validateUserData(userData);
    this.validateEmail(userData.email);

    if (password) {
      this.validatePassword(password);
    }

    logger.info('Creating company user with auth account:', {
      email: userData.email,
      companyId: userData.companyId,
      hasPassword: !!password,
    });

    let authUserId: string | null = null;

    try {
      // デモモード判定
      const isDemoMode =
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        import.meta.env.VITE_DEMO_MODE === 'true';

      // 本番環境でのSupabase Auth統合
      if (password && !isDemoMode) {
        const { data: authData, error: authError } =
          await this.ensureSupabaseClient().auth.admin.createUser({
            email: userData.email,
            password: password,
            email_confirm: true, // メール確認をスキップ
          });

        if (authError) {
          logger.error('Failed to create auth user:', authError);
          throw new Error(`認証アカウントの作成に失敗しました: ${authError.message}`);
        }

        if (!authData.user) {
          throw new Error('認証アカウントの作成に失敗しました');
        }

        authUserId = authData.user.id;
        logger.info('Auth user created successfully:', { authUserId, email: userData.email });
      } else if (isDemoMode) {
        // デモ環境では警告ログのみ
        logger.info('Demo mode: Skipping Supabase Auth user creation', {
          email: userData.email,
          reason: 'Demo environment limitation',
        });
      }

      // company_usersテーブルにレコード作成
      const insertData = {
        ...(authUserId && { id: authUserId }), // Auth UIDがある場合のみ設定
        email: userData.email,
        name: userData.name,
        role: userData.role,
        company_id: userData.companyId,
        created_by: currentUserId,
        status: 'active',
      };

      const { data, error } = await this.ensureSupabaseClient()
        .from('company_users')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        logger.error('Failed to create company user record:', error);

        // Auth userが作成されていた場合はクリーンアップ
        if (authUserId) {
          try {
            await this.ensureSupabaseClient().auth.admin.deleteUser(authUserId);
            logger.info('Cleaned up orphaned auth user:', { authUserId });
          } catch (cleanupError) {
            logger.error('Failed to cleanup auth user:', cleanupError);
          }
        }

        throw new Error(`ユーザーの作成に失敗しました: ${error.message}`);
      }

      logger.info('Company user created successfully:', {
        userId: data.id,
        email: userData.email,
        hasAuth: !!authUserId,
        isDemoMode,
      });

      return this.mapToCompanyUser(data);
    } catch (error) {
      logger.error('User creation failed:', error);

      // エラー時のクリーンアップ
      if (authUserId) {
        try {
          await this.ensureSupabaseClient().auth.admin.deleteUser(authUserId);
          logger.info('Cleaned up auth user after failure:', { authUserId });
        } catch (cleanupError) {
          logger.error('Failed to cleanup auth user after failure:', cleanupError);
        }
      }

      throw error;
    }
  }

  /**
   * 同一会社内での名前重複チェック
   */
  async checkNameExists(name: string, companyId: string, excludeUserId?: string): Promise<boolean> {
    if (!name || !companyId) {
      throw new Error('名前と会社IDが必要です');
    }

    logger.info('Checking name existence:', { name, companyId, excludeUserId });

    const query = this.ensureSupabaseClient()
      .from('company_users')
      .select('id')
      .eq('company_id', companyId)
      .ilike('name', name.trim()); // 大文字小文字を区別しない検索

    // 編集時は自分自身を除外
    if (excludeUserId) {
      query.neq('id', excludeUserId);
    }

    const { data, error } = await query.limit(1);

    if (error) {
      logger.error('Failed to check name existence:', error);
      throw new Error(`名前重複チェックに失敗しました: ${error.message}`);
    }

    const exists = !!data && data.length > 0;
    logger.info('Name existence check result:', { name, exists, count: data?.length || 0 });

    return exists;
  }

  /**
   * 会社のユーザー一覧取得
   */
  async getCompanyUsers(companyId: string): Promise<CompanyUser[]> {
    if (!companyId) {
      throw new Error('会社IDが必要です');
    }

    logger.info('Fetching company users:', { companyId });

    const { data, error } = await this.ensureSupabaseClient()
      .from('company_users')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Failed to fetch company users:', error);
      throw new Error(`ユーザー一覧の取得に失敗しました: ${error.message}`);
    }

    return data.map(user => this.mapToCompanyUser(user));
  }

  /**
   * ユーザー更新
   */
  /**
   * ユーザー更新（FL-209: パスワード更新対応）
   * @param userId - 更新対象のユーザーID
   * @param updateData - 更新データ（name, role, password）
   * @param currentUserId - 操作を実行するユーザーID（パスワード更新時に必須）
   */
  async updateUser(
    userId: string,
    updateData: UpdateUserData,
    currentUserId?: string
  ): Promise<CompanyUser> {
    if (!userId) {
      throw new Error('ユーザーIDが必要です');
    }

    const updatePayload: any = {
      updated_at: new Date().toISOString(),
    };

    if (updateData.name) updatePayload.name = updateData.name;
    if (updateData.role) updatePayload.role = updateData.role;

    logger.info('Updating company user:', {
      userId,
      updateData: { ...updateData, password: updateData.password ? '***' : undefined },
    });

    // パスワード更新処理（FL-209対応）
    if (updateData.password && currentUserId) {
      try {
        const authService = this.getAuthService();
        await authService.updateUserPasswordByAuthorizedUser(
          currentUserId,
          userId,
          updateData.password
        );
        logger.debug('✅ User password updated:', { userId });
      } catch (passwordError) {
        logger.error('❌ Failed to update user password:', passwordError);
        throw passwordError; // パスワード更新失敗時はエラーをスロー
      }
    }

    // company_usersテーブルを更新（パスワード以外）
    const { data, error } = await this.ensureSupabaseClient()
      .from('company_users')
      .update(updatePayload)
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      logger.error('Failed to update user:', error);
      throw new Error(`ユーザーの更新に失敗しました: ${error.message}`);
    }

    return this.mapToCompanyUser(data);
  }

  /**
   * ユーザー削除（論理削除）
   */
  async deleteUser(userId: string): Promise<void> {
    if (!userId) {
      throw new Error('ユーザーIDが必要です');
    }

    logger.info('Deleting company user:', { userId });

    const { error } = await this.ensureSupabaseClient()
      .from('company_users')
      .delete()
      .eq('id', userId);

    if (error) {
      logger.error('Failed to delete user:', error);
      throw new Error(`ユーザーの削除に失敗しました: ${error.message}`);
    }
  }

  /**
   * 招待送信
   */
  async sendInvitation(invitationData: InvitationData, invitedBy: string): Promise<UserInvitation> {
    this.validateEmail(invitationData.email);

    const insertData = {
      email: invitationData.email,
      role: invitationData.role,
      company_id: invitationData.companyId,
      invited_by: invitedBy,
    };

    logger.info('Sending user invitation:', {
      email: invitationData.email,
      companyId: invitationData.companyId,
    });

    const { data, error } = await this.ensureSupabaseClient()
      .from('user_invitations')
      .insert([insertData])
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        // unique constraint violation
        throw new Error('このメールアドレスは既に招待済みです');
      }
      logger.error('Failed to send invitation:', error);
      throw new Error(`招待の送信に失敗しました: ${error.message}`);
    }

    return this.mapToUserInvitation(data);
  }

  /**
   * 招待一覧取得
   */
  async getCompanyInvitations(companyId: string): Promise<UserInvitation[]> {
    if (!companyId) {
      throw new Error('会社IDが必要です');
    }

    const { data, error } = await this.ensureSupabaseClient()
      .from('user_invitations')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Failed to fetch invitations:', error);
      throw new Error(`招待一覧の取得に失敗しました: ${error.message}`);
    }

    return data.map(invitation => this.mapToUserInvitation(invitation));
  }

  /**
   * バリデーションヘルパー
   */
  private validateUserData(userData: CreateUserData): void {
    if (!userData.email || !userData.name || !userData.role || !userData.companyId) {
      throw new Error('必須項目が不足しています');
    }
  }

  private validateEmail(email: string): void {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('無効なメールアドレスです');
    }
  }

  private validatePassword(password: string): void {
    if (!password || password.length < 8) {
      throw new Error('パスワードは8文字以上である必要があります');
    }

    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);

    if (!hasLowercase || !hasNumber) {
      throw new Error('パスワードは英小文字と数字を含む必要があります');
    }
  }

  /**
   * データマッピングヘルパー
   */
  private mapToCompanyUser(data: any): CompanyUser {
    return {
      id: data.id,
      email: data.email,
      name: data.name,
      role: data.role,
      companyId: data.company_id,
      status: data.status,
      landTransporterId: data.land_transporter_id,
      lastLoginAt: data.last_login_at,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      createdBy: data.created_by,
    };
  }

  /**
   * メールアドレス重複チェック
   * 全システム（全会社）でメールアドレスの重複をチェックする
   */
  async checkEmailDuplicate(
    email: string,
    excludeUserId?: string
  ): Promise<{
    isDuplicate: boolean;
  }> {
    if (!email || !email.trim()) {
      return { isDuplicate: false };
    }

    const trimmedEmail = email.trim().toLowerCase();

    logger.info('Checking email duplicate:', {
      email: trimmedEmail,
      excludeUserId,
    });

    try {
      // 1. company_users テーブルから検索
      let companyUsersQuery = this.ensureSupabaseClient()
        .from('company_users')
        .select('id')
        .ilike('email', trimmedEmail);

      if (excludeUserId) {
        companyUsersQuery = companyUsersQuery.neq('id', excludeUserId);
      }

      const { data: companyUsersData, error: companyUsersError } = await companyUsersQuery;

      if (companyUsersError) {
        logger.error('Error checking company_users for duplicate email:', companyUsersError);
        // データベースエラーが発生した場合はエラーをスロー
        throw new Error(
          `メールアドレス重複チェックでエラーが発生しました: ${companyUsersError.message}`
        );
      }

      // company_users で見つかった場合
      if (companyUsersData && companyUsersData.length > 0) {
        return {
          isDuplicate: true,
        };
      }

      // auth.usersテーブルへの直接アクセスは404エラーとなるため削除
      // Supabaseのauth.usersテーブルはRLS (Row Level Security) により、
      // 一般的なクライアントサイドからの直接アクセスが制限されています。
      // そのため、メール重複チェックはcompany_usersテーブルのみで行います。

      // どちらにも見つからない場合
      logger.info('Email is available:', { email: trimmedEmail });
      return { isDuplicate: false };
    } catch (error) {
      logger.error('Unexpected error during email duplicate check:', error);
      // 予期しないエラーが発生した場合は安全側に倒して重複ありとする
      return { isDuplicate: true };
    }
  }

  private mapToUserInvitation(data: any): UserInvitation {
    return {
      id: data.id,
      email: data.email,
      role: data.role,
      companyId: data.company_id,
      invitedBy: data.invited_by,
      invitation_token: data.invitation_token,
      expiresAt: data.expires_at,
      acceptedAt: data.accepted_at,
      createdAt: data.created_at,
    };
  }
}
